import { lazy } from 'react'
import { Navigate, RouteObject } from 'react-router-dom'
import LandingLayout from '../layouts/LandingLayout'
import { tabNames } from './helpers'
import shopRoutes from './shopRouters'

const HomePage = lazy(() =>
    import('../pages/HomePage').then((module) => ({ default: module.default }))
)
const TeacherPage = lazy(() =>
    import('../pages/TeacherPage').then((module) => ({
        default: module.default,
    }))
)
const VicTeacherPage = lazy(() =>
    import('../pages/TeacherPage/locationBasedPages/VicTeacherPage').then(
        (module) => ({ default: module.default })
    )
)
const NswTeacherPage = lazy(() =>
    import('../pages/TeacherPage/locationBasedPages/NswTeacherPage').then(
        (module) => ({ default: module.default })
    )
)
const SchoolPage = lazy(() =>
    import('../pages/SchoolPage').then((module) => ({
        default: module.default,
    }))
)
const VicSchoolPage = lazy(() =>
    import('../pages/SchoolPage/locationBasedPages/VicSchoolPage').then(
        (module) => ({ default: module.default })
    )
)
const NswSchoolPage = lazy(() =>
    import('../pages/SchoolPage/locationBasedPages/NswSchoolPage').then(
        (module) => ({ default: module.default })
    )
)
const ECCCentrePage = lazy(() =>
    import('../pages/ECCPage/centre').then((module) => ({
        default: module.default,
    }))
)
const ECCEducatorPage = lazy(() =>
    import('../pages/ECCPage/educator').then((module) => ({
        default: module.default,
    }))
)
const AboutPage = lazy(() =>
    import('../pages/AboutPage').then((module) => ({ default: module.default }))
)
const HelpDeskPage = lazy(() =>
    import('../pages/HelpDeskPage/v2').then((module) => ({
        default: module.default,
    }))
)
const UserBenefitsPage = lazy(() =>
    import('../pages/UserBenefitsPage').then((module) => ({
        default: module.default,
    }))
)
const TermsOfServicePage = lazy(() =>
    import('../pages/TermsOfServicePage').then((module) => ({
        default: module.default,
    }))
)
const BlogsPage = lazy(() =>
    import('../pages/BlogsPage').then((module) => ({ default: module.default }))
)
const BlogPage = lazy(() =>
    import('../pages/BlogPage').then((module) => ({ default: module.default }))
)
const GuidesPage = lazy(() =>
    import('../pages/GuidesPage').then((module) => ({
        default: module.default,
    }))
)
const ProfessionalLRPage = lazy(() =>
    import('../pages/ProfessionalLRPage').then((module) => ({
        default: module.default,
    }))
)
const CheckoutSuccessPage = lazy(() =>
    import('../pages/CheckoutSuccessPage').then((module) => ({
        default: module.default,
    }))
)
const TeacherFaqPage = lazy(() =>
    import('../pages/FaqPage/TeacherFaqPage').then((module) => ({
        default: module.default,
    }))
)
const SchoolFaqPage = lazy(() =>
    import('../pages/FaqPage/SchoolFaqPage').then((module) => ({
        default: module.default,
    }))
)

const landingRoutes: RouteObject = {
    element: <LandingLayout />,
    children: [
        {
            index: true,
            element: <HomePage />,
            handle: {
                meta: {
                    title: 'Spark Relief Teachers',
                    description:
                        'Casual Relief Teacher smartphone app | Simplifying the CRT booking process.',
                },
            },
        },
        {
            path: tabNames.landing.teachers,
            children: [
                { index: true, element: <TeacherPage /> },
                {
                    path: 'vic',
                    element: <VicTeacherPage />,
                    handle: {
                        meta: {
                            title: 'Teacher - VIC',
                            description:
                                'Find flexible CRT work anywhere in Victoria with Spark Relief Teachers.',
                        },
                    },
                },
                {
                    path: 'nsw',
                    element: <NswTeacherPage />,
                    handle: {
                        meta: {
                            title: 'Teacher - NSW',
                            description:
                                'Join Spark as a casual teacher and earn above the government rate in New South Wales.',
                        },
                    },
                },
            ],
        },
        {
            path: tabNames.landing.schools,
            children: [
                { index: true, element: <SchoolPage /> },
                {
                    path: 'vic',
                    element: <VicSchoolPage />,
                    handle: {
                        meta: {
                            title: 'Schools - VIC',
                            description:
                                'Find affordable CRT cover at your fingertips with the Spark Relief Teachers booking app.',
                        },
                    },
                },
                {
                    path: 'nsw',
                    element: <NswSchoolPage />,
                    handle: {
                        meta: {
                            title: 'Schools - NSW',
                            description:
                                'Source casual teachers without the extra admin through the Spark Relief Teachers booking app.',
                        },
                    },
                },
            ],
        },
        {
            path: tabNames.landing.eccCentre,
            element: <ECCCentrePage />,
            handle: {
                meta: {
                    title: 'Centres - Spark Relief Teachers',
                    description:
                        "Fill your centre's staffing needs stress-free with Spark Relief Teachers. Find and book Early Childhood Educators and manage shifts directly in the app.",
                },
            },
        },
        {
            path: tabNames.landing.eccEducator,
            element: <ECCEducatorPage />,
            handle: {
                meta: {
                    title: 'Educators - Spark Relief Teachers',
                    description:
                        'Take control of your teaching career with flexible work opportunities that fit your schedule. Register with Spark as an Early Childhood Educator today.',
                },
            },
        },
        {
            path: tabNames.landing.about,
            element: <AboutPage />,
            handle: {
                meta: {
                    title: 'About - Spark Relief Teachers',
                    description: 'Find out more about Spark Relief Teachers.',
                },
            },
        },
        {
            path: tabNames.landing.helpDesk,
            element: <HelpDeskPage />,
            handle: {
                meta: {
                    title: 'Help Desk - Spark Relief Teachers',
                    description:
                        'Need help? You can ask a question to our support team here.',
                },
            },
        },
        {
            path: tabNames.landing.userBenefits,
            element: <UserBenefitsPage />,
            handle: {
                meta: {
                    title: 'Spark User / VIT Benefits',
                    description:
                        'Check out the perks and discounts you can receive as a Spark user here!',
                },
            },
        },
        {
            path: tabNames.landing.termsOfService,
            element: <TermsOfServicePage />,
            handle: {
                meta: {
                    title: 'Terms of Registration - Spark Relief Teachers',
                    description:
                        "View Spark Relief Teacher's Terms of Registration here.",
                },
            },
        },
        {
            path: tabNames.landing.blogs,
            children: [
                {
                    index: true,
                    element: <BlogsPage />,
                    handle: {
                        meta: {
                            title: 'Blog - Spark Relief Teachers',
                            description:
                                'Get all the latest from the Spark blog including Spark app updates, casual relief teacher resources, and tips and tricks to navigating the classroom.',
                        },
                    },
                },
                {
                    path: ':blogId',
                    element: <BlogPage />,
                },
            ],
        },
        {
            path: tabNames.landing.guides,
            children: [
                {
                    index: true,
                    element: <Navigate to={'teacher_guides'} replace />,
                },
                {
                    path: 'teacher_guides',
                    element: <GuidesPage />,
                    handle: {
                        meta: {
                            title: 'Teacher Guides - Spark Relief Teachers',
                            description:
                                'Onboarding Teacher Guides for our Teachers to help them in using the Spark Relief Teacher App.',
                        },
                    },
                },
                {
                    path: 'school_guides',
                    element: <GuidesPage />,
                    handle: {
                        meta: {
                            title: 'School Guides - Spark Relief Teachers',
                            description:
                                'Onboarding School Guides for our Schools to help them in using the Spark Relief Teacher App.',
                        },
                    },
                },
            ],
        },
        {
            path: tabNames.landing.pdResources,
            element: <ProfessionalLRPage />,
            handle: {
                meta: {
                    title: 'Professional Development Resources',
                    description:
                        'Looking to complete your professional learning requirements this year? Browse through courses and webinars.',
                },
            },
        },
        {
            path: tabNames.landing.shop,
            children: shopRoutes,
        },
        {
            path: tabNames.landing.checkoutSuccess,
            element: <CheckoutSuccessPage />,
            handle: {
                meta: {
                    title: 'Spark Shop - Checkout Success',
                    description:
                        'Check out our official Spark Relief Teacher merchandise for your day-to-day casual relief teaching needs!',
                },
            },
        },
        {
            path: tabNames.landing.teacherFAQ,
            element: <TeacherFaqPage />,
            handle: {
                meta: {
                    title: 'Teacher FAQ - Spark Relief Teachers',
                },
            },
        },
        {
            path: tabNames.landing.schoolFAQ,
            element: <SchoolFaqPage />,
            handle: {
                meta: {
                    title: 'School FAQ - Spark Relief Teachers',
                },
            },
        },
    ],
}

export default landingRoutes
